import React, { ChangeEvent, useState, useEffect } from "react";
import { Socket } from "socket.io-client";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import OwToggle from "../../../shared/OwToggle/OwToggle";
import { OwFnInput } from "../../../shared/OwFnInput/OwFnInput";
import OwHierarchicalCategorySelector from "../../../shared/OwHierarchicalCategorySelector/OwHierarchicalCategorySelector";
import OwObjectChips from "../../../shared/OwObjectChips/OwObjectChips";
import ICategory from "../../../../models/ICategory";
import IGetCategoriesDTO from "../../../../models/DTO/IGetCategoriesDTO";
import IOnGetCategoriesDTO from "../../../../models/DTO/IOnGetCategoriesDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IUserUI from "../../../../models/IUserUI";
import ISupplierModel from "../../../../models/ISupplierModel";
import OwTranslate from "@components/shared/OwTranslate/OwTranslate";

export type ItemTemplateDownloadFilters = {
    excludeCategoryGuids: string[];
    activeFilter?: "active" | "inactive";
    itemMatchPattern?: string;
    useRegex?: boolean;
};

type Props = {
    closeModalFn: () => void;
    onDownload: (filters: ItemTemplateDownloadFilters) => void;
    catalogGuid: string;
    socket: Socket;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
};

export const ItemTemplateDownloadModal: React.FC<Props> = (props) => {
    const [rootCategories, setRootCategories] = useState<ICategory[]>([]);
    const [excludeCategories, setExcludeCategories] = useState<ICategory[]>([]);
    const [selectorValue, setSelectorValue] = useState<ICategory | undefined>(undefined);
    const [activeFilter, setActiveFilter] = useState<string>("");
    const [itemMatchPattern, setItemMatchPattern] = useState<string>("");
    const [useRegex, setUseRegex] = useState<boolean>(false);

    useEffect(() => {
        const onGetCategories = (data: IOnGetCategoriesDTO) => {
            setRootCategories(data.categories.filter((c) => !c.hide_category));
        };

        props.socket.on(EndpointsEnum.CategoriesOnGetCategories, onGetCategories);

        const payload: IGetCategoriesDTO = {
            userGuid: props.userModel.guid,
            userToken: props.userModel.token,
            supplierGuid: props.supplierModel.guid,
            catalogGuid: props.catalogGuid,
            onlyRootCategories: true,
        };
        props.socket.emit(EndpointsEnum.CategoriesGetCategories, payload);

        return () => {
            props.socket.removeListener(EndpointsEnum.CategoriesOnGetCategories, onGetCategories);
        };
    }, [props.catalogGuid]);

    const handleCategorySelected = (category: ICategory | undefined) => {
        setSelectorValue(undefined);

        if (category && !excludeCategories.some((c) => c.guid === category.guid)) {
            setExcludeCategories((prev) => [...prev, category]);
        }
    };

    const handleRemoveChip = (_chip: any, index: number) => {
        setExcludeCategories((prev) => prev.filter((_, i) => i !== index));
    };

    const handleActiveFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setActiveFilter(e.target.value);
    };

    const handleItemMatchPatternChange = (e: ChangeEvent<HTMLInputElement>) => {
        setItemMatchPattern(e.target.value);
    };

    const handleUseRegexToggle = () => {
        setUseRegex((prev) => !prev);
    };

    const handleDownload = () => {
        const filters: ItemTemplateDownloadFilters = {
            excludeCategoryGuids: excludeCategories.map((c) => c.guid),
        };

        if (activeFilter === "active" || activeFilter === "inactive") {
            filters.activeFilter = activeFilter;
        }

        if (itemMatchPattern.trim()) {
            filters.itemMatchPattern = itemMatchPattern.trim();
            if (useRegex) {
                filters.useRegex = true;
            }
        }

        props.onDownload(filters);
    };

    return (
        <div className={"ItemTemplateDownloadModal p-3"} style={{ minWidth: "500px" }}>
            <div className={"d-flex justify-content-between align-items-start"}>
                <OwTitle leftIconName={"fad fa-download"} title={"item_template"} tag={"h3"} />
                <div>
                    <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={props.closeModalFn} />
                </div>
            </div>

            <div className={"mt-3"}>
                <OwLabel title={"exclude_categories"} isBold={true} />
                <small className={"text-muted d-block mb-2"}>
                    <OwTranslate
                        id={"exclude_categories_description"}
                        defaultMessage={"Items in selected categories (and their subcategories) will be excluded from the download."}
                    />
                </small>

                <OwHierarchicalCategorySelector
                    categories={rootCategories}
                    value={selectorValue}
                    onChange={handleCategorySelected}
                    placeholder="select_category"
                    userModel={props.userModel}
                    supplierModel={props.supplierModel}
                />

                {excludeCategories.length > 0 && (
                    <div className={"mt-2"}>
                        <OwObjectChips chips={excludeCategories} titleProp={"name"} removeChip={handleRemoveChip} />
                    </div>
                )}
            </div>

            <div className={"mt-3"}>
                <OwLabel title={"active_status"} noBottomMargin={true} isBold={true} />
                <OwSelectOption
                    options={[
                        { title: "Active", value: "active" },
                        { title: "Inactive", value: "inactive" },
                    ]}
                    valueProp={"value"}
                    titleProp={"title"}
                    name={"activeFilter"}
                    fn={handleActiveFilterChange}
                    selectedOption={{ activeFilter }}
                    selectedOptionProp={"activeFilter"}
                    showBlankFirst={true}
                />
            </div>

            <div className={"mt-3"}>
                <OwLabel title={"item_match_pattern"} noBottomMargin={true} isBold={true} />
                <OwFnInput
                    fn={handleItemMatchPatternChange}
                    name={"itemMatchPattern"}
                    value={itemMatchPattern}
                    type={"text"}
                    placeholder={"item_match_pattern"}
                />
            </div>

            <div className={"mt-3"}>
                <OwToggle fn={handleUseRegexToggle} name={"useRegex"} label={"use_regex"} active={useRegex} labelOnRight={true} />
            </div>

            <div className={"d-flex justify-content-end mt-3"}>
                <OwButton title={"cancel"} className={"btn-secondary me-2"} fn={props.closeModalFn} />
                <OwButton title={"download"} className={"btn-primary"} leftIconClass={"fad fa-download"} fn={handleDownload} />
            </div>
        </div>
    );
};

export default ItemTemplateDownloadModal;
